"use strict";
import { SearchType } from "@uniswap/client-search/dist/search/v1/api_pb";
import { useMemo } from "react";
import {
  searchTokenToCurrencyInfo,
  useSearchTokensQuery as useSearchTokensRestQuery
} from "data/rest/searchTokens";
import { nativeOnChain } from "constants/topazTokenList";
import { useUserTokens } from "contexts/UserTokensContext";
import { ProtectionResult, SafetyLevel } from "data/graphql/uniswap-data-api/__generated__/types-and-hooks";
import { SUPPORTED_CHAIN_IDS, UniverseChainId } from "features/chains/types";
import { getCurrencySafetyInfo } from "features/dataApi/utils";
import { NUMBER_OF_RESULTS_LONG } from "features/search/SearchModal/constants";
import { useTokenFromAddress } from "hooks/useTokenFromAddress";
import { currencyId } from "utils/uniswapCurrencyId";
import { isAddress } from "utilities/src/addresses";
export function useSearchTokensRest({
  searchQuery,
  chainFilter,
  skip,
  size = NUMBER_OF_RESULTS_LONG
}) {
  const { userAddedTokens } = useUserTokens();
  const variables = useMemo(
    () => ({
      searchQuery: searchQuery ?? void 0,
      chainIds: chainFilter ? [chainFilter] : SUPPORTED_CHAIN_IDS,
      searchType: SearchType.TOKEN,
      page: 1,
      size
    }),
    [searchQuery, chainFilter, size]
  );
  const { data, error, isPending, refetch } = useSearchTokensRestQuery({
    input: variables,
    enabled: !skip,
    userAddedTokens
  });
  const isValidAddress = searchQuery ? isAddress(searchQuery) : false;
  const targetChainId = chainFilter || UniverseChainId.Mainnet;
  const {
    data: blockchainToken,
    loading: blockchainLoading,
    error: blockchainError,
    isValidToken
  } = useTokenFromAddress(
    isValidAddress ? searchQuery : null,
    targetChainId
  );
  const formattedData = useMemo(() => {
    let results = [];
    if (data && data.tokens) {
      results = data.tokens.map((token) => {
        return searchTokenToCurrencyInfo(token);
      }).filter((c) => Boolean(c));
    }
    if (searchQuery && !isAddress(searchQuery)) {
      const query = searchQuery.toLowerCase();
      const chainsToSearch = chainFilter ? [chainFilter] : SUPPORTED_CHAIN_IDS;
      chainsToSearch.forEach((chainId) => {
        const native = nativeOnChain(chainId);
        if (native.symbol?.toLowerCase().includes(query) || native.name?.toLowerCase().includes(query)) {
          results.unshift({
            currencyId: currencyId(native),
            currency: native,
            logoUrl: "/images/tokens/bnb-logo.png",
            safetyLevel: SafetyLevel.Verified,
            safetyInfo: getCurrencySafetyInfo(SafetyLevel.Verified, {
              attackTypes: [],
              result: ProtectionResult.Benign
            }),
            isSpam: false
          });
        }
      });
    }
    if (isValidAddress && blockchainToken && isValidToken) {
      const existingToken = results.find(
        (token) => token.currency.isToken && token.currency.address.toLowerCase() === searchQuery.toLowerCase()
      );
      if (!existingToken) {
        results.unshift(blockchainToken);
      }
    }
    return results.length > 0 ? results : void 0;
  }, [data, blockchainToken, isValidToken, isValidAddress, searchQuery, chainFilter]);
  const loading = isPending || isValidAddress && blockchainLoading;
  const rawError = error || isValidAddress && blockchainError || void 0;
  const combinedError = typeof rawError === "string" ? new Error(rawError) : rawError || void 0;
  return useMemo(
    () => ({
      data: formattedData,
      loading,
      error: combinedError,
      refetch: () => {
        refetch();
        return Promise.resolve();
      }
    }),
    [formattedData, loading, combinedError, refetch]
  );
}
